import React, { FunctionComponent, useRef } from 'react';
import { Object3D, Raycaster, Intersection, Group, Object3DEventMap, FrontSide, DoubleSide } from 'three';
import { hasEntityTapAction, transitionAdjustedRenderOrder } from '../../utils';
import { useDataStore, useSceneStore } from '../../zustand';
import EntityLevelFadeGroup from '../fadeGroups/EntityLevelFadeGroup';
import { IImage, IImageReactProps } from '../r3f-components/component-data-structure';
import { EntityErrorBoundary } from '../r3f-components/components/boundaries';
import {
	useGetAdjustedBorderWidth,
	useGetAdjustedMemoSpatialProps,
	useGetEntityIsHidden,
	useGetSubscribedSceneTransitionCategoryRef,
	useGetSubscribedSceneTransitionPhaseRef,
	useHocPointerDown,
	useOnEntityTestClick,
	useResetEntityTransformsOnPlacement,
} from './hocCustomHooks';
import { useInteraction } from '../TrackerGroups/WorldXRTracker/webxr-hooks';
import { filestore, FsImage } from '@/filestore';
import { useEnableObjectLayer, useGetSceneIndexBySceneId } from './hocCustomHooks';
interface IImageWrapped {
	id: string;
	sceneId: string;
	screenRelative?: boolean;
	raycast?: (_: Raycaster, intersects: Intersection<Object3D<Object3DEventMap>>[]) => void;
}

const withImageBehaviour = (WrappedImage: FunctionComponent<IImageReactProps>): FunctionComponent<IImageWrapped> => {
	const FuncComp: FunctionComponent<IImageWrapped> = ({ id, sceneId, screenRelative = false, raycast }) => {
		const pointerIsDownRef = useRef(false);
		const image = useDataStore(({ componentsById }) => componentsById?.[id] as IImage) || {};
		const shadowsEnabled = useDataStore(({ shadowsEnabled }) => shadowsEnabled ?? true);
		const sceneTransCatRef = useGetSubscribedSceneTransitionCategoryRef(sceneId);
		// ! Don't remove this line - it's a quick-fix to deliberately cause a re-render to keep render order in sync with Video components
		const sceneTransitionCategory = useSceneStore(({ transitionCategory }) => transitionCategory[sceneId]);
		const sceneTransPhaseRef = useGetSubscribedSceneTransitionPhaseRef(sceneId);
		const entityIsHidden = useGetEntityIsHidden(id) && screenRelative;
		const pointerGroupRef = useRef<Group>(null)
		const sceneIndex = useGetSceneIndexBySceneId(sceneId);

		// console.log('Image > sceneTransCatRef', sceneTransCatRef)

		useOnEntityTestClick({ sceneId, entity: image, sceneTransCatRef, sceneTransPhaseRef });

		useEnableObjectLayer(pointerGroupRef, hasEntityTapAction(image), typeof sceneIndex === 'undefined' ? undefined : sceneIndex + 1);

		// Ensures that any transforms due to previous animation presets are cleared out
		useResetEntityTransformsOnPlacement(id);

		// Get entity values
		const borderWidth = useGetAdjustedBorderWidth(image, screenRelative);
		const renderOrder = transitionAdjustedRenderOrder(sceneTransCatRef, (image.renderOrder ?? 0) * 20);
		const { position, scale, rotation, zeroTuple3 } = useGetAdjustedMemoSpatialProps(image, screenRelative);
		const { borderRadius, borderRgba, filestoreId, opacity, hasAlpha, curvature, castShadow = false, receiveShadow = false, isSnappedToTarget = false } = image;

		const onPointerDown = useHocPointerDown({ pointerIsDownRef, sceneTransCatRef, sceneTransPhaseRef, sceneId, entity: image, screenRelative });

		useInteraction(pointerGroupRef, 'onSelect', e => {
			onPointerDown(e.intersections);
			pointerIsDownRef.current = false;
		})

		const imageUrl = filestore.get<FsImage>(filestoreId)?.getImageUrl();

		//console.log('Image renderOrder: ', renderOrder);
		return (
			<group name={`transformGroup-${id}`} position={position}>
				<group rotation={rotation}>
					<group name={`transformGroup-inner_${id}`}>
						<group name="ENTITY_VISIBLE_GROUP" visible={!entityIsHidden}>
							<EntityLevelFadeGroup name={`fadeGroup-${id}`} sceneId={sceneId} entityId={id} position={zeroTuple3} screenRelative={screenRelative}>
								<EntityErrorBoundary renderOrder={renderOrder} scale={scale} rotation={zeroTuple3}>
									<group name={`toggleVisibilityGroup-${id}`}>
										<group ref={pointerGroupRef} name={`sceneId_${sceneId}`} onPointerUp={() => (pointerIsDownRef.current = false)}>
											<WrappedImage
												id={id}
												renderOrder={renderOrder}
												rotation={zeroTuple3}
												scale={scale}
												position={zeroTuple3}
												imageUrl={imageUrl}
												curvature={curvature}
												borderWidth={borderWidth}
												borderRadius={borderRadius}
												borderRgba={borderRgba}
												hasAlpha={hasAlpha}
												opacity={opacity}
												side={isSnappedToTarget ? FrontSide :  DoubleSide}
												castShadow={shadowsEnabled && castShadow}
												receiveShadow={shadowsEnabled && receiveShadow}
												raycast={raycast}
												onPointerDown={(e) => onPointerDown(e.intersections)}
											/>
										</group>
									</group>
								</EntityErrorBoundary>
							</EntityLevelFadeGroup>
						</group>
					</group>
				</group>
			</group>
		);
	};
	return FuncComp;
};

export default withImageBehaviour;
